import { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { DefaultError } from "@tanstack/react-query"
import { NotFoundError } from "api/errors"
import { getText } from "api/get-text"
import { getNeedSignTerms } from "modules/account-root/selectors"
import { ERROR, LOADING, OK, queryStatus } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import { useNotifications } from "hooks/notifications"
import { useGetQuery } from "hooks/queries"
import { Creators } from "model/apiWrapper"
import { useAccountAlias, useUserId } from "resources/account"
import type { SkillCatalogEntry, SkillCatalogResponse, SkillSection, SkillTier } from "connected-components/skills/types"
import { buildSkillSections } from "connected-components/skills/utils/sections"

const EMPTY_SKILLS: SkillCatalogEntry[] = []
const EMPTY_CATEGORIES: string[] = []

export interface SkillFileEntry {
  path: string
  size: number
  isText: boolean
}

interface SkillFilesResponse {
  files: SkillFileEntry[]
}

const EMPTY_FILES: SkillFileEntry[] = []

export function getLicensedSkillsQueryKey(accountAlias: string): [string, string] {
  return ["skills-licensed", accountAlias]
}

export function getProprietarySkillsQueryKey(accountAlias: string): [string, string] {
  return ["skills-proprietary", accountAlias]
}

export function getOwnSkillsQueryKey(accountAlias: string, userId: string): [string, string, string] {
  return ["skills-own", accountAlias, userId]
}

export function getSharedSkillsQueryKey(accountAlias: string, userId: string): [string, string, string] {
  return ["skills-shared", accountAlias, userId]
}

export function getSkillSettingsQueryKey(accountAlias: string): [string, string] {
  return ["skill-settings", accountAlias]
}

export function useLicensedSkills() {
  const accountAlias = useAccountAlias()
  return useGetQuery<SkillCatalogResponse>({ url: `/api/${accountAlias}/skills/licensed`, key: getLicensedSkillsQueryKey(accountAlias) })
}

export function useProprietarySkills() {
  const accountAlias = useAccountAlias()
  return useGetQuery<SkillCatalogResponse>({ url: `/api/${accountAlias}/skills/proprietary`, key: getProprietarySkillsQueryKey(accountAlias) })
}

export function useOwnSkills() {
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  return useGetQuery<SkillCatalogResponse>({ url: `/api/${accountAlias}/skills/own`, key: getOwnSkillsQueryKey(accountAlias, userId) })
}

export function useSharedSkills() {
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  return useGetQuery<SkillCatalogResponse>({ url: `/api/${accountAlias}/skills/shared`, key: getSharedSkillsQueryKey(accountAlias, userId) })
}

export function useDeleteOwnSkill() {
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const dispatch = useDispatch()
  const notifications = useNotifications()
  const queryClient = useQueryClient()

  const mutation = useMutation<unknown, DefaultError, string>({
    mutationFn: (skillId) =>
      new Promise((resolve, reject) => {
        dispatch(Creators.delete(`/api/${accountAlias}/skills/${encodeURIComponent(skillId)}`, undefined, resolve, reject))
      }).catch((error) => {
        if (error instanceof NotFoundError) return undefined
        throw error
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: getOwnSkillsQueryKey(accountAlias, userId) }),
        queryClient.invalidateQueries({ queryKey: getSkillSettingsQueryKey(accountAlias) }),
      ]),
    onError: () => {
      notifications.error("Couldn't delete this skill. Please try again.")
    },
  })

  return { deleteSkill: mutation.mutateAsync, deletingSkillId: mutation.isPending ? mutation.variables : null }
}

function combineStatuses(...statuses: DataStatus[]): DataStatus {
  if (statuses.includes(ERROR)) return ERROR
  if (statuses.includes(LOADING)) return LOADING
  return OK
}

export interface SkillCatalog {
  sections: SkillSection[]
  categoryOrder: string[]
  status: DataStatus
  ownStatus: DataStatus
  sharedStatus: DataStatus
}

export function useSkillCatalog(): SkillCatalog {
  const licensed = useLicensedSkills()
  const proprietary = useProprietarySkills()
  const own = useOwnSkills()
  const shared = useSharedSkills()
  const licensedSkills = licensed.data?.skills ?? EMPTY_SKILLS
  const proprietarySkills = proprietary.data?.skills ?? EMPTY_SKILLS
  const ownSkills = own.data?.skills ?? EMPTY_SKILLS
  const sharedSkills = shared.data?.skills ?? EMPTY_SKILLS
  const sections = useMemo(
    () => [...buildSkillSections(licensedSkills, proprietarySkills, ownSkills, sharedSkills)].reverse(),
    [licensedSkills, proprietarySkills, ownSkills, sharedSkills],
  )
  const licensedCategories = licensed.data?.categories ?? EMPTY_CATEGORIES
  const proprietaryCategories = proprietary.data?.categories ?? EMPTY_CATEGORIES
  const categoryOrder = useMemo(
    () => [...licensedCategories, ...proprietaryCategories.filter((category) => !licensedCategories.includes(category))],
    [licensedCategories, proprietaryCategories],
  )
  const status = combineStatuses(queryStatus(licensed), queryStatus(proprietary))
  return { sections, categoryOrder, status, ownStatus: queryStatus(own), sharedStatus: queryStatus(shared) }
}

function skillFilesUrl(accountAlias: string, tier: SkillTier, skillId: string): string {
  return `/api/${accountAlias}/skills/files/${tier}/${encodeURIComponent(skillId)}`
}

export function getSkillFilesQueryKey(accountAlias: string, userId: string, tier: SkillTier, skillId: string): [string, string, string, SkillTier, string] {
  return ["skill-files", accountAlias, userId, tier, skillId]
}

export function getSkillFileContentQueryKey(accountAlias: string, userId: string, tier: SkillTier, skillId: string, path: string | null): [string, string, string, SkillTier, string, string | null] {
  return ["skill-file-content", accountAlias, userId, tier, skillId, path]
}

export interface SkillFiles {
  status: DataStatus
  files: SkillFileEntry[]
}

export function useSkillFiles(tier: SkillTier, skillId: string): SkillFiles {
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const query = useGetQuery<SkillFilesResponse>({
    url: skillFilesUrl(accountAlias, tier, skillId),
    key: getSkillFilesQueryKey(accountAlias, userId, tier, skillId),
  })
  return { status: queryStatus(query), files: query.data?.files ?? EMPTY_FILES }
}

export interface SkillFileContent {
  status: DataStatus
  text: string | null
  /** The response status when the read failed, so the viewer can say which failure it was. */
  httpStatus: number | null
}

class SkillFileError extends Error {
  constructor(readonly httpStatus: number, path: string) {
    super(`Skill file ${path} did not load (${httpStatus})`)
  }
}

export function useSkillFileContent(tier: SkillTier, skillId: string, path: string | null): SkillFileContent {
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const needSignTerms = useSelector(getNeedSignTerms)
  const url = `${skillFilesUrl(accountAlias, tier, skillId)}/content?path=${encodeURIComponent(path ?? "")}`
  const query = useQuery<string>({
    queryKey: getSkillFileContentQueryKey(accountAlias, userId, tier, skillId, path),
    enabled: path !== null,
    queryFn: async () => {
      const result = await getText(url, { account: accountAlias, userId, getNeedSignTerms: () => needSignTerms })
      if (result.kind !== "text") throw new SkillFileError(result.status, path ?? "")
      return result.text
    },
  })
  if (path === null) return { status: OK, text: null, httpStatus: null }
  const httpStatus = query.error instanceof SkillFileError ? query.error.httpStatus : null
  return { status: queryStatus(query), text: query.data ?? null, httpStatus }
}
